import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Pencil, Plus, Search as SearchIcon, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { FieldMassPanel } from "@/components/FieldMass";
import type { MassTarget } from "@/components/MassPanel";
import { locationMassTarget, massLocationUpdate } from "@/features/warehousing/mass";
import type { MassBatch, MassPreview } from "@patronix/domain/mass";
import { inputClass } from "@/components/Panel";
import { ImportPanel } from "@/features/import/ImportPanel";
import { locationDetailQuery, locationListQuery, structureKeys, warehouseListQuery, type LocationRow } from "@/features/warehousing/api";
import { LocationFlags, LocationPanel } from "@/features/warehousing/LocationPanel";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/session";
import { locationListColumns } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Locations of one warehouse. Keyset pages of 50 on (tenant, warehouse, code);
// prefix search is a range on the same index. One request per page, one per
// detail (prefetched on hover). Warehouse picker reuses the cached warehouse
// list (one small request, shared with the Warehouses screen).

const invalidateAfterImport = [structureKeys.all] as const;

interface Search extends ViewSearch {
  wh: string;
  q: string;
  inactive: boolean;
  loc?: string | undefined;
  /** The import panel is open (replaces the location detail). */
  import?: "locations" | undefined;
}

export const Route = createFileRoute("/_app/warehousing/locations")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      wh: typeof raw.wh === "string" ? raw.wh : "",
      q: typeof raw.q === "string" ? raw.q.slice(0, 30) : "",
      inactive: raw.inactive === true || raw.inactive === "true",
    };
    if (typeof raw.loc === "string" && raw.loc) s.loc = raw.loc;
    if (raw.import === "locations") s.import = raw.import;
    return { ...s, ...viewSearch(locationListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ wh: search.wh, q: search.q, inactive: search.inactive, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    // Without ?wh the default depends on the active company (resolved in the
    // component); the location page then loads right after the warehouse list.
    const warehouses = context.queryClient.ensureInfiniteQueryData(warehouseListQuery(false));
    if (deps.wh) {
      await Promise.all([
        warehouses,
        context.queryClient.ensureInfiniteQueryData(locationListQuery({ warehouseId: deps.wh, q: deps.q, inactive: deps.inactive, ...viewQuery(locationListColumns, deps) })),
      ]);
    } else {
      await warehouses;
    }
  },
  component: LocationsPage,
});

function LocationsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("inventory.structure.manage");
  const canImport = useCan("core.data.import");

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const warehouseList = useInfiniteQuery(warehouseListQuery(false));
  const warehouseRows = warehouseList.data?.pages.flatMap((p) => p.rows) ?? [];
  const { company } = useActiveCompany();
  // Default: first warehouse of the company the user works in.
  const warehouseId = search.wh || (warehouseRows.find((w) => w.company_id === company?.id) ?? warehouseRows[0])?.id || "";
  const warehouse = warehouseRows.find((w) => w.id === warehouseId);

  const view = listView(locationListColumns, search, setSearch);
  const listParams = { warehouseId, q: search.q, inactive: search.inactive, ...view.query };
  const query = useInfiniteQuery(locationListQuery(listParams));
  const [mass, setMass] = useState<{ target: MassTarget; count: number | null } | null>(null);
  const tr = t as unknown as (key: string, o?: Record<string, unknown>) => string;

  const columns: Column<LocationRow>[] = [
    {
      key: "code",
      header: t("locations.colCode"),
      width: "minmax(120px,0.9fr)",
      cell: (r, selected) => (
        <span
          className={cn(
            "font-mono text-xs",
            selected && "font-semibold text-accent-ink",
            !r.is_active && "text-ink-muted line-through decoration-ink-faint",
          )}
        >
          {r.code}
        </span>
      ),
    },
    {
      key: "description",
      header: t("locations.colDescription"),
      width: "minmax(160px,1.5fr)",
      className: "text-ink-soft",
      cell: (r) => r.description ?? "—",
    },
    { key: "zone", header: t("locations.colZone"), width: "minmax(70px,0.5fr)", cell: (r) => r.zone ?? "—" },
    {
      key: "flags",
      header: t("locations.colFlags"),
      width: "minmax(170px,1fr)",
      cell: (r) => <LocationFlags location={r} />,
    },
  ];

  if (warehouseRows.length === 0 && !warehouseList.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader canManage={false} onNew={() => {}} onGenerate={() => {}} />
        <div className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-surface p-6">
          <p className="font-medium">{t("locations.noWarehouses")}</p>
          <Link
            to="/warehousing/warehouses"
            search={{ inactive: false, warehouse: "new" }}
            className="h-9 rounded-xl bg-accent px-3.5 py-2 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
          >
            {t("warehouses.newWarehouse")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <PageHeader
        canManage={canManage && !!warehouse?.is_active}
        onNew={() => setSearch({ loc: "new", import: undefined })}
        onGenerate={() => setSearch({ loc: "generate", import: undefined })}
      />

      <Toolbar
        search={search}
        setSearch={setSearch}
        warehouseId={warehouseId}
        warehouses={warehouseRows.map((w) => ({ id: w.id, label: `${w.company?.code ?? ""} · ${w.code} — ${w.name}` }))}
      />

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("locations.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.loc}
          onSelect={(r) => {
            setMass(null);
            setSearch({ loc: r.id, import: undefined });
          }}
          onClear={() => setSearch({ loc: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(locationDetailQuery(r.id))}
          emptyTitle={t("locations.emptyTitle")}
          emptyBody={t("locations.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ q: "", inactive: false, loc: undefined, ...noView })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={560}
          view={view}
          tools={{
            id: "locations",
            title: t("locations.title"),
            onNew: canManage && warehouse?.is_active ? () => setSearch({ loc: "new", import: undefined }) : undefined,
            newLabel: t("locations.newLocation"),
            onImport: canManage && warehouse?.is_active && canImport ? () => setSearch({ import: "locations", loc: undefined }) : undefined,
            mass:
              canManage && warehouse
                ? [
                    {
                      key: "update",
                      label: t("mass.locations.update"),
                      icon: <Pencil size={15} />,
                      open: (target: MassTarget, count: number | null) => {
                        setSearch({ loc: undefined, import: undefined });
                        setMass({ target, count });
                      },
                    },
                  ]
                : [],
          }}
        />
        <DetailPanel
          label={mass ? t("mass.locations.updateTitle") : search.import ? t("import.title") : t("locations.detailLabel")}
          open={!!mass || !!search.loc || !!search.import}
          placeholder={<DetailPlaceholder title={t("locations.selectPrompt")} hint={t("locations.selectPromptHint")} />}
        >
          {mass && warehouse ? (
            <FieldMassPanel
              key={`${mass.target.kind}:${mass.count ?? "all"}`}
              title={t("mass.locations.updateTitle")}
              hint={t("mass.locations.updateHint")}
              targetText={mass.count === null ? tr("mass.locations.all", { warehouse: warehouse.code }) : tr("mass.locations.checkedRows", { count: mass.count })}
              fileBase="locations"
              errorMessage={(e) => (e as { message?: string } | null)?.message ?? String(e)}
              onClose={() => setMass(null)}
              onFinished={() => queryClient.invalidateQueries({ queryKey: structureKeys.all })}
              fields={[
                { key: "is_active", label: t("mass.locations.active"), kind: "bool" },
                { key: "is_receivable", label: t("mass.locations.receivable"), kind: "bool" },
                { key: "is_pickable", label: t("mass.locations.pickable"), kind: "bool" },
                { key: "is_blocked", label: t("mass.locations.blocked"), kind: "bool" },
                { key: "zone", label: t("mass.locations.zone"), kind: "text", upper: true, pattern: /^[A-Z0-9][A-Z0-9_-]{0,15}$/, clearable: true },
              ]}
              preview={(c) => massLocationUpdate(warehouse.company_id, locationMassTarget(mass.target, listParams), c, true) as Promise<MassPreview>}
              runBatch={(c, after) =>
                massLocationUpdate(warehouse.company_id, locationMassTarget(mass.target, listParams), c, false, after) as Promise<MassBatch>
              }
              applyLabel={(n) => tr("mass.locations.apply", { count: n })}
            />
          ) : search.import ? (
            <ImportPanel
              entities={["locations"]}
              initial="locations"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.loc &&
            warehouseId && (
              <LocationPanel
                key={search.loc}
                warehouseId={warehouseId}
                locationId={search.loc}
                onClose={() => setSearch({ loc: undefined })}
                onCreated={(id) => setSearch({ loc: id ?? undefined })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

function PageHeader({
  canManage,
  onNew,
  onGenerate,
}: {
  canManage: boolean;
  onNew: () => void;
  onGenerate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <header className="flex flex-wrap items-end gap-3">
      <div className="flex grow flex-col gap-0.5">
        <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
          {t("nav.warehousing.title")} <span aria-hidden="true">/</span> {t("nav.warehousing.locations")}
        </nav>
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("locations.title")}</h1>
      </div>
      {canManage && (
        <>
          <button
            type="button"
            onClick={onGenerate}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-surface pl-3 pr-4 font-medium text-ink hover:bg-surface-hover"
          >
            <Sparkles size={16} aria-hidden="true" />
            {t("locations.generate")}
          </button>
          <button
            type="button"
            onClick={onNew}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("locations.newLocation")}
          </button>
        </>
      )}
    </header>
  );
}

function Toolbar({
  search,
  setSearch,
  warehouseId,
  warehouses,
}: {
  search: Search;
  setSearch: (p: Partial<Search>) => void;
  warehouseId: string;
  warehouses: { id: string; label: string }[];
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-2 text-[13px] text-ink-soft">
        <span className="sr-only">{t("locations.warehouse")}</span>
        <select
          aria-label={t("locations.warehouse")}
          value={warehouseId}
          onChange={(e) => setSearch({ wh: e.target.value, loc: undefined })}
          className={cn(inputClass, "w-auto min-w-[240px] font-medium")}
        >
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.label}
            </option>
          ))}
        </select>
      </label>
      <label className="ml-1 flex items-center gap-2 text-[13px] text-ink-soft">
        <input
          type="checkbox"
          checked={search.inactive}
          onChange={(e) => setSearch({ inactive: e.target.checked })}
          className="size-4 accent-(--accent)"
        />
        {t("locations.showInactive")}
      </label>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <SearchIcon size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value.toUpperCase())}
          placeholder={t("locations.searchPlaceholder")}
          aria-label={t("locations.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent font-mono text-[13px] uppercase text-ink outline-none placeholder:font-sans placeholder:normal-case placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}
